import { useCallback } from 'react';

import { useMemberId } from '@trello/authentication';
import { useBoardId } from '@trello/id-context';
import { useSharedStateSelector } from '@trello/shared-state';

import { useBoardTypeQuery } from './BoardTypeQuery.generated';
import { cachedInboxIdsState } from './cachedInboxIdsState';
import type { InboxIds } from './Inbox.types';

export const useMemberInboxIds = (): Partial<InboxIds> => {
  const memberId = useMemberId();
  const inboxIds = useSharedStateSelector(
    cachedInboxIdsState,
    useCallback((state) => state[memberId], [memberId]),
  );

  return inboxIds || {};
};

export const useIsInboxBoardPassContextVals = ({
  boardId,
  memberId,
}: {
  boardId: string | null;
  memberId: string;
}) => {
  const isInboxBoard = useSharedStateSelector(
    cachedInboxIdsState,
    useCallback(
      (state) => {
        const inboxIds = state[memberId];
        return Boolean(inboxIds?.idBoard && inboxIds.idBoard === boardId);
      },
      [boardId, memberId],
    ),
  );

  return isInboxBoard;
};

export const useIsInboxBoardWithId = (boardId: string) => {
  const memberId = useMemberId();
  const { data } = useBoardTypeQuery({
    variables: { boardId },
    waitOn: ['CurrentBoardInfo'],
    skip: !boardId,
  });
  const isInboxBoard = data?.board?.type === 'inbox';
  // fallback to board type check if inbox board is not cached
  // TODO: remove this once we have inbox available in the quickload
  return useIsInboxBoardPassContextVals({ boardId, memberId }) || isInboxBoard;
};

export const useIsInboxBoard = () => {
  const boardId = useBoardId();
  return useIsInboxBoardWithId(boardId);
};
